import type { Nullable } from '../../types/common'
import { checkMobileVersion } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import BaseComponent from '../base-component/base-component'

class Lookbook extends BaseComponent {
  pinSelector: string
  blockSelector: string
  modalSelector: string
  carouselSelector: string
  desktopContentSelector: string
  mobileContentSelector: string

  animationPausedAttribute: string
  notHighlightedAttribute: string

  desktopContent: Nullable<HTMLElement>
  mobileContent: Nullable<HTMLElement>
  pins: HTMLElement[]

  highlightedBlockId: string

  resizeObserver: ResizeObserver

  constructor() {
    super()

    this.pinSelector = '[data-lookbook-pin]'
    this.blockSelector = '[data-lookbook-block]'
    this.modalSelector = '[data-lookbook-modal]'
    this.carouselSelector = '[data-lookbook-carousel]'
    this.desktopContentSelector = '[data-lookbook-desktop-content]'
    this.mobileContentSelector = '[data-lookbook-mobile-content]'

    this.animationPausedAttribute = 'animation-paused'
    this.notHighlightedAttribute = 'not-highlighted'

    this.desktopContent = this.querySelector(this.desktopContentSelector)
    this.mobileContent = this.querySelector(this.mobileContentSelector)
    this.pins = [...this.querySelectorAll(this.pinSelector)] as HTMLElement[]

    this.resizeObserver = new ResizeObserver(this.handleResize)
  }

  connectedCallback(): void {
    this.pins.forEach(pin => {
      pin.addEventListener('mouseenter', this.handlePinEnter)
      pin.addEventListener('mouseleave', this.handlePinLeave)
      pin.addEventListener('click', this.handlePinClick)
    })

    this.resizeObserver.observe(this)
  }

  disconnectedCallback(): void {
    this.pins.forEach(pin => {
      pin.removeEventListener('mouseenter', this.handlePinEnter)
      pin.removeEventListener('mouseleave', this.handlePinLeave)
      pin.removeEventListener('click', this.handlePinClick)
    })

    this.resizeObserver.disconnect()

    window.removeEventListener('click', this.handleOutsideClick)
  }

  handlePinEnter = (): void => {
    this.setAnimationPausedState(true)
  }

  handlePinLeave = (): void => {
    if (this.highlightedBlockId) {
      return
    }

    this.setAnimationPausedState(false)
  }

  handleResize = debounce(() => {
    this.showBlockById(this.highlightedBlockId)
  }, 500)

  handlePinClick = (event: MouseEvent): void => {
    const pin = event.target
    const blockId = pin.dataset.blockId

    this.showBlockById(blockId)

    window.addEventListener('click', this.handleOutsideClick)
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const pin = event.target.closest(this.pinSelector)

    if (pin) {
      return
    }

    this.showBlockById()

    window.removeEventListener('click', this.handleOutsideClick)
  }

  showBlockById(blockId?: string): void {
    this.updateSelectedStateForPins(blockId)

    if (blockId) {
      this.setHighlightBlockById(blockId)
      this.setAnimationPausedState(true)
    } else {
      this.resetHighlight()
      this.setAnimationPausedState(false)
    }

    this.highlightedBlockId = blockId
  }

  updateSelectedStateForPins(blockId?: string) {
    this.pins.forEach(pin => {
      const isSelectedPin = pin.dataset.blockId === blockId

      pin.toggleAttribute('is-selected', isSelectedPin)
    })
  }

  setAnimationPausedState(isAnimationPaused: boolean): void {
    this.toggleAttribute(this.animationPausedAttribute, isAnimationPaused)
  }

  setHighlightBlockById(blockId?: string): void {
    this.blocks.forEach((block, index) => {
      const isHighlighted = block.dataset.blockId === blockId

      if (isHighlighted) {
        this.scrollToProductByIndex(index)
      }

      block.toggleAttribute(this.notHighlightedAttribute, !isHighlighted)
    })
  }

  scrollToProductByIndex(index: number) {
    const block = this.blocks[index]
    const carousel = block.closest(this.carouselSelector)

    if (carousel) {
      carousel.scrollToIndex(index)
    } else {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  resetHighlight() {
    this.blocks.forEach(block => {
      block.removeAttribute(this.notHighlightedAttribute)
    })
  }

  get blocks(): HTMLElement[] {
    const isMobile = checkMobileVersion()
    const content = isMobile ? this.mobileContent : this.desktopContent

    const modal = content.querySelector(this.modalSelector)
    const node = modal ? modal.element : content

    const blocks = [...node.querySelectorAll(this.blockSelector)]

    return blocks
  }
}

export default Lookbook
